// src/components/ChangePasswordForm.js
// S4c ítem 3 — cambio de contraseña desde Mi Perfil.
// Flujo: reautenticar con la actual → updatePassword (SDK cliente; el servidor
// aplica la política del proyecto cuando POLICY ON esté activo) → escribir
// users/{uid}.passwordChangedAt (auto-edición permitida por rules; la clave
// passwordRotation NO es editable por el usuario) → signOut para re-login.
import React, { useState } from 'react';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, signOut } from 'firebase/auth';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { validateNewPassword, MIN_PASSWORD_LENGTH } from '../lib/passwordPolicy';
import { t } from '../lib/i18n';

const AUTH_ERR = {
    'auth/wrong-password': 'password.err.wrongCurrent',
    'auth/invalid-credential': 'password.err.wrongCurrent',
    'auth/invalid-login-credentials': 'password.err.wrongCurrent',
    'auth/weak-password': 'password.err.weak',
    'auth/password-does-not-meet-requirements': 'password.err.weak',
};

const ChangePasswordForm = ({ auth, db, user }) => {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [errors, setErrors] = useState([]);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        const v = validateNewPassword({ current, next, confirm, email: user?.email });
        setErrors(v.errors);
        if (!v.ok) return;
        setBusy(true);
        try {
            const cu = auth.currentUser;
            const cred = EmailAuthProvider.credential(cu.email, current);
            await reauthenticateWithCredential(cu, cred);
            await updatePassword(cu, next);
            try { await updateDoc(doc(db, 'users', cu.uid), { passwordChangedAt: serverTimestamp() }); }
            catch (err) { console.error('passwordChangedAt no escrito:', err); }
            setDone(true);
            setTimeout(() => signOut(auth), 1500);
        } catch (err) {
            console.error('Cambio de contraseña falló:', err?.code, err?.message);
            setErrors([AUTH_ERR[err?.code] || 'password.err.generic']);
        } finally {
            setBusy(false);
        }
    };

    if (done) return <p role="status" className="text-green-700 font-semibold">{t('password.ok')}</p>;

    return (
        <form onSubmit={onSubmit} className="space-y-3 max-w-md" aria-label={t('password.section.title')}>
            <p className="text-sm text-gray-500">{t('password.section.help')}</p>
            <div>
                <label htmlFor="pw-current" className="block text-sm font-medium text-gray-600 mb-1">{t('password.current')}</label>
                <input id="pw-current" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="w-full p-2 border rounded" />
            </div>
            <div>
                <label htmlFor="pw-next" className="block text-sm font-medium text-gray-600 mb-1">{t('password.new')}</label>
                <input id="pw-next" type="password" autoComplete="new-password" required minLength={MIN_PASSWORD_LENGTH} value={next} onChange={(e) => setNext(e.target.value)} className="w-full p-2 border rounded" />
            </div>
            <div>
                <label htmlFor="pw-confirm" className="block text-sm font-medium text-gray-600 mb-1">{t('password.confirm')}</label>
                <input id="pw-confirm" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full p-2 border rounded" />
            </div>
            {errors.length > 0 && (
                <ul className="text-sm text-red-600 list-disc pl-5">
                    {errors.map((k) => <li key={k}>{t(k)}</li>)}
                </ul>
            )}
            <button type="submit" disabled={busy} className="px-4 py-2 font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {busy ? t('password.saving') : t('password.submit')}
            </button>
        </form>
    );
};

export default ChangePasswordForm;
